import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChevronLeft, Building2 } from "lucide-react";

const YASAMARU_LOGIN = "/yasamaru/jump.png";

export default function TenantLogin() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { setSession } = useAuth();

  const [tenant, setTenant] = useState(null);
  const [tenantErr, setTenantErr] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/tenants/by-slug/${slug}`).then(({ data }) => setTenant(data))
      .catch((e) => setTenantErr(formatApiError(e.response?.data?.detail) || "企業が見つかりません"));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { tenant_slug: slug, email, password });
      setSession(data);
      const dest = data.user.role === "tenant_admin" ? `/t/${slug}/admin` : `/t/${slug}/`;
      nav(dest, { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-soft-mint">
      {/* Left form */}
      <div className="flex items-center justify-center p-6 md:p-10 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-800 text-sm mb-6" data-testid="back-home-link">
            <ChevronLeft size={16} /> ホーム
          </Link>

          <div className="flex items-center gap-2 text-stone-500 text-sm mb-1">
            <Building2 size={16} />
            {tenant ? <span data-testid="tenant-name">{tenant.name}</span> : tenantErr ? <span className="text-rose-500" data-testid="tenant-error">{tenantErr}</span> : <span>読み込み中...</span>}
          </div>
          <h1 className="font-display text-3xl font-black text-stone-900">ログイン</h1>
          <p className="text-stone-500 text-sm mt-1">あなたの会社専用のログイン画面です。</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block">
              <span className="text-sm font-semibold text-stone-700">メールアドレス</span>
              <input data-testid="tenant-login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full px-3.5 py-2.5 rounded-xl border border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-emerald-300 outline-none" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-stone-700">パスワード</span>
              <input data-testid="tenant-login-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full px-3.5 py-2.5 rounded-xl border border-stone-200 bg-stone-50 focus:bg-white focus:ring-2 focus:ring-emerald-300 outline-none" />
            </label>
            {err && <div data-testid="tenant-login-error" className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 rounded-xl text-sm">{err}</div>}
            <button data-testid="tenant-login-submit" type="submit" disabled={loading || !tenant}
              className="w-full rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-bold py-3.5">
              {loading ? "ログイン中..." : "ログイン"}
            </button>
          </form>

          <div className="mt-6 text-xs text-stone-500">
            このURL: <code className="bg-stone-100 px-1.5 py-0.5 rounded">yasamarutime.com/t/{slug}/login</code>
          </div>
        </div>
      </div>

      {/* Right mascot */}
      <div className="hidden md:flex items-center justify-center relative overflow-hidden">
        <div className="blob bg-emerald-200" style={{ width: 360, height: 360, top: -40, left: -40 }} />
        <div className="blob bg-orange-200" style={{ width: 320, height: 320, bottom: -40, right: -40 }} />
        <img src={YASAMARU_LOGIN} alt="やさまる" className="relative z-10 w-80 float-slow drop-shadow-xl" />
      </div>
    </div>
  );
}
